/**
 * 姿势数据模型（P0-1d）
 *
 * 三层合成：预设(presetId → 65 骨骼欧拉弧度) ⊕ 关节控制(controls, 稀疏, 角度制) ⊕ 单骨覆盖(boneOverrides, 稀疏)
 *
 * 与竞品的一处**有意分歧**：不烘焙
 *   竞品在 characterRig.boneOverrides 里存了**完整 65 骨骼的烘焙结果**（实测 146/151 角色为 65 项、
 *   9567 个值全为 3 元素数组、其中 20% 是 <1e-9 的浮点噪声 —— 可见是算出来的而非作者写的）。
 *   我们只存 intent，渲染时实时计算，理由三条：
 *     1. 双真源必然漂移：preset/controls 与烘焙结果不一致时，没有客观答案
 *     2. 编辑功能本来就必须实现映射函数，烘焙省不下任何代码
 *     3. 体积：83 人群戏 × 65 骨 × 3 float 会让工程文件近乎翻倍
 *   因此本模型的 boneOverrides 恢复其字面语义：**稀疏**，仅在用户手改单根骨骼时出现。
 */

import { CONTROL_NAMES, BONE_NAMES } from './rigConstants';

/** 欧拉角，单位弧度（与姿势库一致） */
export type EulerRadians = readonly [x: number, y: number, z: number];

/**
 * 骨骼名到欧拉角的映射。
 * 用 Record<string, ...> 而非固定键：实测竞品有角色携带 69 根骨骼（超出 65 基础集），
 * 我们必须能读进未知骨骼而不是解析失败。
 */
export type BoneMap = Readonly<Record<string, EulerRadians>>;

export type ControlName = (typeof CONTROL_NAMES)[number];
export type BoneName = string;

/** 稀疏关节控制，单位角度；缺失项视为 0（实测竞品存在仅 4 项的稀疏字典） */
export type RigControls = Partial<Record<ControlName, number>>;

/** 稀疏单骨覆盖，弧度；只在手改某根骨骼时写入 */
export type BoneOverrides = Record<string, EulerRadians>;

/** 姿势引用：977 库的 id（如 `5109_0`）或具名内置姿势（如 `stand`） */
export type PosePresetId = string;

/** 具名内置姿势：不在 977 库内，由本地实现提供 */
export const NAMED_POSES = ['stand'] as const;
export type NamedPose = (typeof NAMED_POSES)[number];

export const MAX_CONTROL_DEGREES = 180;
export const MIN_CONTROL_DEGREES = -180;

export interface CharacterRig {
  rigType: 'mixamo';
  posePresetId: PosePresetId | null;
  controls: RigControls;
  boneOverrides: BoneOverrides;
}

export function isControlName(v: string): v is ControlName {
  return (CONTROL_NAMES as readonly string[]).includes(v);
}

export function isKnownBone(v: string): boolean {
  return (BONE_NAMES as readonly string[]).includes(v);
}

export function clampDegrees(v: number): number {
  if (!Number.isFinite(v)) {
    return 0;
  }
  return Math.min(MAX_CONTROL_DEGREES, Math.max(MIN_CONTROL_DEGREES, v));
}

export function degreesToRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * 关节控制 → 骨骼通道映射。
 *
 * ⚠️ 这是**待标定项**（蓝图 R10）：轴序与正负号取决于 Mixamo 各骨骼的局部坐标系与 rest pose，
 * 必须在真实模型上逐个标定，不能凭推理定稿。此处给出的是语义起点：
 *   - shoulder.* 控制的是**上臂 LeftArm/RightArm**（抬臂），不是锁骨 LeftShoulder
 *   - 竞品把 A-pose 补偿存成普通 controls（实测 4 项：左右肩 pitch ∓7 / spread ±7），
 *     我们不照抄这个隐式约定，改为 rest-pose 常量（见 REST_COMPENSATION），
 *     避免"用户没动滑杆却已经是 ±7"这种不可解释的状态。
 */
export interface ControlBinding {
  bone: BoneName;
  axis: 0 | 1 | 2;
  sign: 1 | -1;
}

export const CONTROL_BINDINGS: Readonly<Record<ControlName, ControlBinding>> = {
  'body.pitch': { bone: 'mixamorigHips', axis: 0, sign: 1 },
  'body.roll': { bone: 'mixamorigHips', axis: 2, sign: 1 },
  'body.yaw': { bone: 'mixamorigHips', axis: 1, sign: 1 },
  'torso.pitch': { bone: 'mixamorigSpine', axis: 0, sign: 1 },
  'torso.roll': { bone: 'mixamorigSpine', axis: 2, sign: 1 },
  'torso.yaw': { bone: 'mixamorigSpine', axis: 1, sign: 1 },
  'head.pitch': { bone: 'mixamorigHead', axis: 0, sign: 1 },
  'head.roll': { bone: 'mixamorigHead', axis: 2, sign: 1 },
  'head.yaw': { bone: 'mixamorigHead', axis: 1, sign: 1 },
  'leftShoulder.pitch': { bone: 'mixamorigLeftArm', axis: 0, sign: 1 },
  'leftShoulder.spread': { bone: 'mixamorigLeftArm', axis: 2, sign: -1 },
  'leftShoulder.twist': { bone: 'mixamorigLeftArm', axis: 1, sign: 1 },
  'rightShoulder.pitch': { bone: 'mixamorigRightArm', axis: 0, sign: 1 },
  'rightShoulder.spread': { bone: 'mixamorigRightArm', axis: 2, sign: 1 },
  'rightShoulder.twist': { bone: 'mixamorigRightArm', axis: 1, sign: -1 },
  'leftElbow.bend': { bone: 'mixamorigLeftForeArm', axis: 0, sign: 1 },
  'rightElbow.bend': { bone: 'mixamorigRightForeArm', axis: 0, sign: -1 },
  'leftHip.pitch': { bone: 'mixamorigLeftUpLeg', axis: 0, sign: 1 },
  'leftHip.spread': { bone: 'mixamorigLeftUpLeg', axis: 2, sign: 1 },
  'leftHip.twist': { bone: 'mixamorigLeftUpLeg', axis: 1, sign: 1 },
  'rightHip.pitch': { bone: 'mixamorigRightUpLeg', axis: 0, sign: 1 },
  'rightHip.spread': { bone: 'mixamorigRightUpLeg', axis: 2, sign: -1 },
  'rightHip.twist': { bone: 'mixamorigRightUpLeg', axis: 1, sign: -1 },
  'leftKnee.bend': { bone: 'mixamorigLeftLeg', axis: 0, sign: -1 },
  'rightKnee.bend': { bone: 'mixamorigRightLeg', axis: 0, sign: 1 },
} as const;

/**
 * 合成最终骨骼姿态。
 * 规则：预设弧度 + 控制增量（角度→弧度）；boneOverrides 对**单根骨骼整体**最终覆盖。
 * 纯函数，不改动入参；未知骨骼名一并保留（不丢弃）。
 */
export function composeBones(
  preset: BoneMap | null,
  controls: RigControls,
  overrides: BoneOverrides = {},
): Record<string, EulerRadians> {
  const out: Record<string, [number, number, number]> = {};

  for (const [bone, e] of Object.entries(preset ?? {})) {
    out[bone] = [e[0], e[1], e[2]];
  }

  for (const entry of Object.entries(controls)) {
    const key = entry[0];
    const deg = entry[1];
    if (deg === undefined || !isControlName(key)) {
      continue;
    }
    // 显式处理映射缺项：若将来 CONTROL_NAMES 新增而忘记配 binding，
    // 这里应“跳过该控件”而不是抛错或崩在 undefined 上。
    const b: ControlBinding | undefined = CONTROL_BINDINGS[key];
    if (b === undefined) {
      continue;
    }
    const current = out[b.bone] ?? [0, 0, 0];
    const next: [number, number, number] = [current[0], current[1], current[2]];
    next[b.axis] += degreesToRadians(clampDegrees(deg) * b.sign);
    out[b.bone] = next;
  }

  for (const [bone, e] of Object.entries(overrides)) {
    out[bone] = [e[0], e[1], e[2]];
  }

  return out;
}

/** 把任意来源的骨骼字典规范化为 [x,y,z] 元组，容忍缺字段与非法值 */
export function normalizeBoneMap(raw: unknown): BoneMap {
  if (typeof raw !== 'object' || raw === null) {
    return {};
  }
  const out: Record<string, EulerRadians> = {};
  for (const [bone, value] of Object.entries(raw as Record<string, unknown>)) {
    const arr = readTriple(value);
    if (arr !== null) {
      out[bone] = arr;
    }
  }
  return out;
}

/**
 * 兼容两种历史形态：
 *  - 姿势库：`{ rotation: { x, y, z } }`
 *  - 竞品 boneOverrides / 我方内部：`[x, y, z]`
 */
function readTriple(value: unknown): EulerRadians | null {
  if (Array.isArray(value) && value.length === 3) {
    const [x, y, z] = value as [unknown, unknown, unknown];
    if (typeof x === 'number' && typeof y === 'number' && typeof z === 'number') {
      return [x, y, z];
    }
    return null;
  }
  if (typeof value === 'object' && value !== null) {
    const rec = value as Record<string, unknown>;
    const rot = (rec.rotation ?? rec) as Record<string, unknown>;
    const { x, y, z } = rot;
    if (typeof x === 'number' && typeof y === 'number' && typeof z === 'number') {
      return [x, y, z];
    }
  }
  return null;
}

/** 解析姿势库条目：`{ bones: [ { Bone: { rotation } } ] }` → BoneMap */
export function parsePoseFile(json: unknown): BoneMap {
  if (typeof json !== 'object' || json === null) {
    return {};
  }
  const bones = (json as Record<string, unknown>).bones;
  if (!Array.isArray(bones) || bones.length === 0) {
    return {};
  }
  const first = bones[0];
  if (typeof first !== 'object' || first === null) {
    return {};
  }
  const out: Record<string, EulerRadians> = {};
  for (const [bone, value] of Object.entries(first as Record<string, unknown>)) {
    const t = readTriple(value);
    if (t !== null) {
      out[bone] = t;
    }
  }
  return out;
}

export function emptyRig(): CharacterRig {
  return { rigType: 'mixamo', posePresetId: null, controls: {}, boneOverrides: {} };
}

/** 控件名集合是否合法（用于加载外部工程时校验，不认识的键要报出来而非静默忽略） */
export function unknownControlKeys(controls: Record<string, unknown>): string[] {
  return Object.keys(controls).filter((k) => !isControlName(k));
}
